// ═══════════════════════════════════════════════════════════════════════════
// PAGE: GESTION DES GROUPES & PERMISSIONS + USER MODAL
// Migration S4.2 du plan TS+Vite (2026-05-10) : .js → .ts strict.
// ═══════════════════════════════════════════════════════════════════════════
//
// Définit `showUserModal` (consommé par page-admin-users via bare-ref
// déclaré dans core-globals.d.ts).

// Picker chips réutilisé depuis la modale d'édition d'action (consultés
// RACI) pour saisir les managers d'une équipe. Le bloc rendu via
// raci-${field} + chips-${field} est ancré pour le dropdown.
import {
  _getRaciIds,
  _removeRaciChip,
  showRaciPicker,
} from './component-action-modal/raci-picker.js';
import { buildUserModalBody } from './page-user-modal-fragments.js';
import { wireInvitationButtons } from './page-user-modal-invite.js';

interface UserGroupMember {
  id: number;
  name?: string;
  trigram?: string | null;
}

export interface UserGroup {
  id: number;
  name: string;
  is_admin_group?: boolean;
  permissions?: string[];
  members?: UserGroupMember[];
}

interface AvailablePermsResp {
  permissions: string[];
  // Sous-ensemble autorisé par le palier d'abonnement du tenant (= tous les
  // codes si pas de plafond). Sert à griser les droits hors palier.
  tier_allowed?: string[];
  // Phrase d'explication par code de droit, affichée en infobulle au survol.
  descriptions?: Record<string, string>;
}

// Descriptions des droits (code → phrase), peuplées au chargement de la page
// et lues par la modale de groupe pour l'infobulle au survol de chaque droit.
let _permDescriptions: Record<string, string> = {};

export interface UserEntityFull {
  id: number;
  name: string;
  first_name?: string | null;
  last_name?: string | null;
  trigram?: string | null;
  email?: string | null;
  role?: string | null;
  is_team?: boolean;
  is_external?: boolean;
  is_active?: boolean;
  team_ids?: number[];
  manager_ids?: number[];
  group_ids?: number[];
  departure_date?: string | null;
  object_rights?: string | null;
}

interface PermDomainInfo {
  code: string;
  label: string;
}

declare global {
  interface Window {
    renderUserGroupsPage: typeof renderUserGroupsPage;
    showUserGroupModal: typeof showUserGroupModal;
  }
}

pages.adminUserGroups = async function () {
  setHeader(
    t('user_groups.page_title'),
    `<button class="btn btn-primary ml-auto" id="btn-new-group">${esc(t('user_groups.btn.add'))}</button>`,
  );
  setContent(`<div class="loading">${esc(t('common.loading'))}</div>`);
  try {
    const [groups, availResp, users] = await Promise.all([
      api.getUserGroups() as Promise<UserGroup[]>,
      api.getAvailablePermissions() as Promise<AvailablePermsResp>,
      // Scope gestion : DMO (compte admin technique) est membre du groupe
      // Administrateur — il doit rester visible ici pour gérer son appartenance,
      // tout en restant hors des sélecteurs d'affectation (règle 12).
      api.getUsers({ manage: true }) as Promise<UserEntityFull[]>,
    ]);
    const allPerms = availResp.permissions || [];
    const tierAllowed = availResp.tier_allowed || allPerms;
    _permDescriptions = availResp.descriptions || {};
    renderUserGroupsPage(groups, allPerms, users, tierAllowed);
    document.getElementById('btn-new-group')?.addEventListener('click', () => {
      showUserGroupModal(null, allPerms, users, tierAllowed);
    });
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : t('common.error');
    setContent(`<div class="error">${esc(msg)}</div>`);
  }
};

function renderUserGroupsPage(
  groups: UserGroup[],
  allPerms: ReadonlyArray<string>,
  users: ReadonlyArray<UserEntityFull>,
  tierAllowed: ReadonlyArray<string>,
): void {
  const totalPerms = allPerms.length;
  let sortKey: 'name' | 'perms' | 'members' = 'name';
  let sortDir: 'asc' | 'desc' = 'asc';

  const sortGroups = (arr: ReadonlyArray<UserGroup>): UserGroup[] =>
    [...arr].sort((a, b) => {
      let va: string | number;
      let vb: string | number;
      switch (sortKey) {
        case 'name':
          va = (a.name || '').toLowerCase();
          vb = (b.name || '').toLowerCase();
          break;
        case 'perms':
          va = a.is_admin_group ? totalPerms : (a.permissions || []).length;
          vb = b.is_admin_group ? totalPerms : (b.permissions || []).length;
          break;
        case 'members':
          va = (a.members || []).length;
          vb = (b.members || []).length;
          break;
        default:
          va = (a.name || '').toLowerCase();
          vb = (b.name || '').toLowerCase();
      }
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

  const th = (key: 'name' | 'perms' | 'members', label: string): string => {
    const active = sortKey === key;
    const arrow = active ? (sortDir === 'asc' ? '↑' : '↓') : '↕';
    return `<th class="sortable${active ? ' sorted' : ''}" data-sort-grp="${key}" style="cursor:pointer;user-select:none">${esc(label)} <span class="sort-arrow" style="font-size:10px">${arrow}</span></th>`;
  };

  const buildRows = (): string =>
    sortGroups(groups)
      .map((g) => {
        const badge = g.is_admin_group
          ? `<span style="background:#fef3c7;color:#92400e;font-size:10px;padding:2px 8px;border-radius:8px;font-weight:500">${esc(t('user_groups.label.admin'))}</span>`
          : '';
        const permCount = g.is_admin_group ? totalPerms : (g.permissions || []).length;
        const memberCount = (g.members || []).length;
        const disableDelete = g.is_admin_group
          ? `disabled title="${esc(t('user_groups.del.protected_title'))}"`
          : `title="${esc(t('common.delete'))}"`;
        return `<tr>
      <td style="cursor:pointer" data-click-group="${g.id}"><strong>${esc(g.name)}</strong> ${badge}</td>
      <td style="font-size:12px;color:var(--text3)">${permCount} / ${totalPerms}</td>
      <td style="font-size:12px;color:var(--text3)">${memberCount}</td>
      <td>
        <button class="btn btn-secondary btn-icon" data-edit-group="${g.id}" title="${esc(t('common.edit'))}">✎</button>
        <button class="btn btn-danger btn-icon" data-del-group="${g.id}" data-group-name="${esc(g.name)}" style="margin-left:4px" ${disableDelete}>✕</button>
      </td>
    </tr>`;
      })
      .join('');

  const emptyRow = `<tr><td colspan="4" class="empty">${esc(t('user_groups.empty'))}</td></tr>`;

  const renderTable = (): string => `<div class="table-wrap"><table id="groups-table">
        <thead><tr>
          ${th('name', t('user_groups.col.name'))}
          ${th('perms', t('user_groups.col.permissions'))}
          ${th('members', t('user_groups.col.members'))}
          <th></th>
        </tr></thead>
        <tbody id="groups-tbody">${buildRows() || emptyRow}</tbody>
      </table></div>`;

  setContent(`<div class="card" id="groups-card">${renderTable()}</div>`);

  document.getElementById('groups-card')?.addEventListener('click', (e: Event) => {
    const target = e.target as HTMLElement | null;
    if (!target) return;

    const sortTh = target.closest<HTMLElement>('[data-sort-grp]');
    if (sortTh) {
      const key = sortTh.dataset['sortGrp'] as 'name' | 'perms' | 'members';
      if (sortKey === key) sortDir = sortDir === 'asc' ? 'desc' : 'asc';
      else {
        sortKey = key;
        sortDir = 'asc';
      }
      const tbody = document.getElementById('groups-tbody');
      if (tbody) tbody.innerHTML = buildRows() || emptyRow;
      document.querySelectorAll<HTMLElement>('[data-sort-grp]').forEach((h) => {
        const a = h.dataset['sortGrp'] === sortKey;
        const arrow = h.querySelector('.sort-arrow');
        if (arrow) arrow.textContent = a ? (sortDir === 'asc' ? '↑' : '↓') : '↕';
        h.classList.toggle('sorted', a);
      });
      return;
    }
    const clickGroup = target.closest<HTMLElement>('[data-click-group]');
    if (clickGroup && !target.closest('button')) {
      const id = parseInt(clickGroup.dataset['clickGroup'] || '0');
      const g = groups.find((x) => x.id === id);
      if (g) showUserGroupModal(g, allPerms, users, tierAllowed);
      return;
    }
    const editBtn = target.closest<HTMLElement>('[data-edit-group]');
    if (editBtn) {
      const id = parseInt(editBtn.dataset['editGroup'] || '0');
      const g = groups.find((x) => x.id === id);
      if (g) showUserGroupModal(g, allPerms, users, tierAllowed);
      return;
    }
    const delBtn = target.closest<HTMLButtonElement>('[data-del-group]');
    if (delBtn && !delBtn.hasAttribute('disabled')) {
      const id = parseInt(delBtn.dataset['delGroup'] || '0');
      const name = delBtn.dataset['groupName'] || '';
      openModal(
        t('user_groups.modal.delete.title'),
        `<p>${t('user_groups.modal.delete.message', { name })}</p>`,
        async () => {
          try {
            await api.deleteUserGroup(id);
            closeModal();
            toast(t('user_groups.toast.deleted'));
            void pages.adminUserGroups?.({});
          } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : t('common.error');
            toast(msg, 'error');
          }
        },
        t('common.delete'),
      );
    }
  });
}

function _permsByDomain(
  allPerms: ReadonlyArray<string>,
): Record<string, Array<{ code: string; action: string }>> {
  const byDomain: Record<string, Array<{ code: string; action: string }>> = {};
  for (const p of allPerms) {
    const dotIdx = p.indexOf('.');
    if (dotIdx < 0) continue;
    const domain = p.slice(0, dotIdx);
    const action = p.slice(dotIdx + 1);
    if (!byDomain[domain]) byDomain[domain] = [];
    byDomain[domain]?.push({ code: p, action });
  }
  return byDomain;
}

function showUserGroupModal(
  group: UserGroup | null,
  allPerms: ReadonlyArray<string>,
  allUsers: ReadonlyArray<UserEntityFull>,
  tierAllowed?: ReadonlyArray<string>,
): void {
  const isNew = !group;
  const isAdminGroup = !!(group && group.is_admin_group);

  // Droits autorisés par le palier d'abonnement du tenant (= tous si pas de
  // plafond). Les droits hors palier ne sont PAS affichés du tout (un admin
  // client ne peut pas accorder au-delà de son palier) → la matrice n'est
  // construite que sur les droits du palier.
  const tierSet = new Set<string>(tierAllowed ?? allPerms);

  const byDomain = _permsByDomain(allPerms.filter((c) => tierSet.has(c)));
  const domains: ReadonlyArray<PermDomainInfo> = window.PERMISSION_DOMAINS || [];
  const actionLabels: Record<string, string> = window.PERMISSION_ACTION_LABELS || {
    read: 'Lecture',
    write: 'Écriture',
    analyze: 'Analyser',
  };
  const currentPerms = new Set<string>(group ? group.permissions || [] : []);
  if (isAdminGroup) {
    allPerms.forEach((p) => currentPerms.add(p));
  }
  // Suivi des bugs : droit universel → coché par défaut sur un nouveau groupe
  // (l'admin peut décocher). Les groupes existants sont backfillés par migration.
  if (isNew) {
    if (allPerms.includes('bugs.read')) currentPerms.add('bugs.read');
    if (allPerms.includes('bugs.write')) currentPerms.add('bugs.write');
  }

  const knownCodes = new Set(domains.map((d) => d.code));
  const extraDomains: PermDomainInfo[] = Object.keys(byDomain)
    .filter((c) => !knownCodes.has(c))
    .map((c) => ({ code: c, label: c }));
  const displayDomains: ReadonlyArray<PermDomainInfo> = [...domains, ...extraDomains];

  const matrixRows = displayDomains
    .map((d) => {
      const perms = byDomain[d.code] || [];
      if (!perms.length) return '';
      const cells = perms
        .map((p) => {
          const checked = currentPerms.has(p.code) ? 'checked' : '';
          const disabled = isAdminGroup ? 'disabled' : '';
          const cursor = isAdminGroup ? 'not-allowed' : 'pointer';
          const label = actionLabels[p.action] || p.action;
          const desc = _permDescriptions[p.code] || '';
          const titleAttr = desc ? ` title="${esc(desc)}"` : '';
          return `<label${titleAttr} style="display:inline-flex;align-items:center;gap:6px;margin-right:16px;cursor:${cursor};font-weight:400">
        <input type="checkbox" class="ug-perm-chk" data-perm-code="${esc(p.code)}" ${checked} ${disabled}>
        <span style="font-size:13px">${esc(label)}</span>
      </label>`;
        })
        .join('');
      return `<tr>
      <td style="font-weight:500;padding:6px 10px;white-space:nowrap">${esc(d.label)}</td>
      <td style="padding:6px 10px">${cells}</td>
    </tr>`;
    })
    .join('');

  const memberIds = new Set((group?.members || []).map((m) => m.id));
  const individuals = (allUsers || []).filter((u) => !u.is_team);
  const memberCheckboxes =
    individuals
      .map(
        (u) => `
    <label style="display:flex;align-items:center;gap:6px;margin-bottom:2px;cursor:pointer;font-weight:400;font-size:13px">
      <input type="checkbox" class="ug-member-chk" value="${u.id}" ${memberIds.has(u.id) ? 'checked' : ''}>
      <span>${esc(u.name)}${u.trigram ? ` <span style="color:var(--text3);font-size:11px">(${trigramHtml(u, { extraStyle: 'font-size:11px', placeholder: '' })})</span>` : ''}</span>
    </label>`,
      )
      .join('') ||
    `<span style="color:var(--text3);font-size:12px">${esc(t('user_groups.no_individuals'))}</span>`;

  const adminWarning = isAdminGroup
    ? `<div style="margin-bottom:12px;padding:8px 12px;background:#fef3c7;color:#92400e;border-radius:var(--radius);font-size:13px">
         ${esc(t('user_groups.admin_warning'))}
       </div>`
    : '';

  const body = `
    ${adminWarning}
    <div class="form-group">
      <label>${esc(t('user_groups.field.name'))}</label>
      <input type="text" id="ug-name" value="${esc(group ? group.name : '')}" maxlength="64" placeholder="${esc(t('user_groups.field.name.placeholder'))}" ${isAdminGroup ? 'disabled' : ''}>
    </div>
    <div class="form-group" style="margin-top:16px">
      <label>${esc(t('user_groups.field.permissions'))}</label>
      <div style="border:1px solid var(--border);border-radius:var(--radius);overflow:hidden">
        <table style="width:100%;border-collapse:collapse">
          <tbody>${matrixRows}</tbody>
        </table>
      </div>
    </div>
    <div class="form-group" style="margin-top:16px">
      <label>${esc(t('user_groups.field.members'))}</label>
      <div style="max-height:220px;overflow-y:auto;padding:8px 12px;border:1px solid var(--border);border-radius:var(--radius);background:var(--bg3)">
        ${memberCheckboxes}
      </div>
    </div>`;

  openWideModal(
    isNew ? t('user_groups.modal.create.title') : t('user_groups.modal.update.title'),
    body,
    async () => {
      const nameEl = document.getElementById('ug-name') as HTMLInputElement | null;
      const name = nameEl?.value.trim() || '';
      if (!isAdminGroup && !name) {
        toast(t('user_groups.error.name_required'), 'error');
        return;
      }

      const checkedPerms = Array.from(
        document.querySelectorAll<HTMLInputElement>('.ug-perm-chk:checked'),
      )
        .map((el) => el.dataset['permCode'] || '')
        // Filet : ne jamais soumettre un droit hors palier (le backend le
        // rejetterait en 400). Les cases hors palier sont déjà désactivées.
        .filter((c) => c !== '' && tierSet.has(c));
      const permissions = isAdminGroup ? allPerms.slice() : checkedPerms;
      const member_ids = Array.from(
        document.querySelectorAll<HTMLInputElement>('.ug-member-chk:checked'),
      ).map((el) => parseInt(el.value));

      const payload = {
        name: isAdminGroup && group ? group.name : name,
        permissions,
        member_ids,
      };
      try {
        if (isNew) await api.createUserGroup(payload);
        else if (group) await api.updateUserGroup(group.id, payload);
        closeModal();
        toast(isNew ? t('user_groups.toast.created') : t('user_groups.toast.updated'));
        void pages.adminUserGroups?.({});
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : t('common.error');
        toast(msg, 'error');
      }
    },
    isNew ? t('common.add_short') : t('common.save'),
  );
}

interface UserPayload {
  /** Patronyme (le backend recompose l'affichage avec `first_name`). */
  name: string;
  first_name: string | null;
  trigram: string | null;
  email: string | null;
  role: string;
  is_team: boolean;
  is_external: boolean;
  is_active: boolean;
  manager_ids: number[];
  team_ids: number[];
  object_rights: string;
  group_ids?: number[];
  departure_date?: string | null;
  // Confirmation du dépassement de plafond contractuel (B-2). Renvoyé à true
  // après que l'utilisateur a accepté le complément de facturation.
  confirm_overage?: boolean;
}

/**
 * Lit le formulaire de la modale utilisateur et en construit le payload API.
 *
 * Extrait de `showUserModal` (cliquet taille-fonction) : ~40 lignes de lecture
 * du DOM qui n'avaient besoin d'aucun état de la closure, sauf `canEditGroups`
 * (sans le droit d'écriture sur les groupes, les cases sont désactivées et
 * l'affectation ne doit PAS être envoyée). Retourne `null` — après avoir
 * signalé l'erreur — quand le nom est vide, seul champ obligatoire.
 */
function _readUserModalPayload(canEditGroups: boolean): UserPayload | null {
  const val = (id: string): string =>
    (document.getElementById(id) as HTMLInputElement | HTMLSelectElement | null)?.value.trim() ||
    '';
  const checked = (id: string, fallback: boolean): boolean =>
    (document.getElementById(id) as HTMLInputElement | null)?.checked ?? fallback;
  // `name` = le PATRONYME côté API (août 2026) ; le prénom a son propre champ,
  // et le nom affiché se recompose côté serveur.
  const name = val('um-last-name');
  if (!name) {
    toast(t('user_groups.error.name_required'), 'error');
    return null;
  }
  const isTeamVal = checked('um-is-team', false);
  const payload: UserPayload = {
    name,
    // Une équipe n'a pas de prénom : son `name` est un libellé, pas une
    // identité (le backend le remet à NULL de son côté).
    first_name: isTeamVal ? null : val('um-first-name') || null,
    trigram: val('um-trigram').toUpperCase() || null,
    email: val('um-email') || null,
    role: val('um-role') || 'member',
    is_team: isTeamVal,
    is_external: checked('um-is-external', false),
    // Équipes : toujours actives. Pour les individus, on lit la checkbox.
    is_active: isTeamVal ? true : checked('um-is-active', true),
    team_ids: isTeamVal ? [] : _checkedIds('um-team-ids'),
    // Les managers ne sont saisissables qu'en mode équipe ; ignore le contenu
    // résiduel des chips quand l'utilisateur a décoché `is_team`.
    manager_ids: isTeamVal ? _getRaciIds('managers') : [],
    object_rights: isTeamVal ? 'DM' : val('um-object-rights') || 'DM',
  };
  if (!isTeamVal && canEditGroups) payload.group_ids = _checkedIds('um-group-ids');
  if (!isTeamVal) payload.departure_date = val('um-departure-date') || null;
  return payload;
}

/** Ids des cases cochées d'un groupe de checkboxes (par attribut `name`). */
function _checkedIds(inputName: string): number[] {
  return Array.from(
    document.querySelectorAll<HTMLInputElement>(`input[name="${inputName}"]:checked`),
  ).map((el) => parseInt(el.value));
}

async function showUserModal(
  user: UserEntityFull | null,
  allUsers: ReadonlyArray<UserEntityFull>,
): Promise<void> {
  const isNew = !user;
  const isTeamChecked = user ? !!user.is_team : false;
  // is_active : par défaut TRUE à la création, sinon valeur courante.
  // Les équipes (is_team) sont toujours considérées actives.
  const isActiveChecked = isNew ? true : !!user?.is_active;
  const teams = (allUsers || []).filter((u) => u.is_team && (!user || u.id !== user.id));
  const currentTeamIds = user && !user.is_team ? user.team_ids || [] : [];
  const currentGroupIds = new Set(user && !user.is_team ? user.group_ids || [] : []);
  const currentDeparture = user && user.departure_date ? user.departure_date : '';
  const currentObjectRights = (user && user.object_rights) || 'DM';
  // Pool des managers possibles : utilisateurs individuels (is_team=False),
  // hors l'utilisateur édité lui-même (une équipe ne peut pas se manager).
  const managerCandidates = (allUsers || []).filter(
    (u) => !u.is_team && (!user || u.id !== user.id),
  );
  const currentManagerIds = user && user.is_team ? user.manager_ids || [] : [];

  let availableGroups: UserGroup[] = [];
  let canEditGroups = false;
  if (!isTeamChecked && window.hasPermission && window.hasPermission('user_groups.read')) {
    try {
      availableGroups = (await api.getUserGroups()) as UserGroup[];
      canEditGroups = !!(window.hasPermission && window.hasPermission('user_groups.write'));
    } catch (e: unknown) {
      safeWarn('api.getUserGroups (user modal)', e);
    }
  }

  // Corps HTML de la modale : rendu pur délégué à page-user-modal-fragments
  // (toutes les entrées immuables passées explicitement ; les cases/chips y sont
  // reconstruites à l'identique).
  const body = buildUserModalBody({
    user,
    isNew,
    isTeamChecked,
    isActiveChecked,
    teams,
    currentTeamIds,
    managerCandidates,
    currentManagerIds,
    availableGroups,
    currentGroupIds,
    canEditGroups,
    currentDeparture,
    currentObjectRights,
  });

  openWideModal(
    isNew ? t('user_modal.create.title') : t('user_modal.update.title'),
    body,
    async () => {
      const payload = _readUserModalPayload(canEditGroups);
      if (!payload) return;
      const isTeamVal = payload.is_team;
      if (!isNew && user && !isTeamVal && user.is_team) {
        const usedBy = (allUsers || []).filter(
          (u) => !u.is_team && (u.team_ids || []).includes(user.id),
        );
        if (usedBy.length) {
          toast(
            t('user_modal.error.team_referenced', {
              users: usedBy.map((u) => u.name).join(', '),
              plural: usedBy.length > 1 ? 'nent' : 't',
            }),
            'error',
          );
          return;
        }
      }
      const persist = async (): Promise<void> => {
        if (isNew) await api.createUser(payload);
        else if (user) await api.updateUser(user.id, payload);
        closeModal();
        toast(isNew ? t('user_modal.toast.created') : t('user_modal.toast.updated'));
        _cache.users = null;
        void pages.adminUsers?.({});
      };
      try {
        await persist();
      } catch (err: unknown) {
        const e = err as { status?: number; detail?: { code?: string; message?: string } };
        // B-2 : dépassement du plafond contractuel → demander confirmation du
        // complément de facture, puis recréer avec confirm_overage = true.
        if (isNew && e.status === 409 && e.detail?.code === 'max_users_exceeded') {
          openModal(
            t('user_modal.overage.title'),
            `<p>${esc(e.detail.message || t('user_modal.overage.message'))}</p>`,
            async () => {
              payload.confirm_overage = true;
              try {
                await persist();
              } catch (err2: unknown) {
                const msg2 = err2 instanceof Error ? err2.message : t('common.error');
                toast(msg2, 'error');
              }
            },
            t('user_modal.overage.confirm'),
          );
          return;
        }
        const msg = err instanceof Error ? err.message : t('common.error');
        toast(msg, 'error');
      }
    },
    isNew ? t('common.add_short') : t('common.save'),
  );

  setTimeout(() => {
    const chk = document.getElementById('um-is-team') as HTMLInputElement | null;
    if (chk)
      chk.addEventListener('change', () => {
        const grp = document.getElementById('um-team-group');
        if (grp) grp.style.display = chk.checked ? 'none' : '';
        const invGrp = document.getElementById('um-invite-group');
        if (invGrp) invGrp.style.display = chk.checked ? 'none' : '';
        const ggrp = document.getElementById('um-groups-group');
        if (ggrp) ggrp.style.display = chk.checked ? 'none' : '';
        const dgrp = document.getElementById('um-departure-group');
        if (dgrp) dgrp.style.display = chk.checked ? 'none' : '';
        const orgrp = document.getElementById('um-object-rights-group');
        if (orgrp) orgrp.style.display = chk.checked ? 'none' : '';
        // Prénom : une équipe n'en porte pas (son `name` est un libellé) —
        // le backend le remet à NULL de toute façon.
        const firstNameGrp = document.getElementById('um-first-name-group');
        if (firstNameGrp) firstNameGrp.style.display = chk.checked ? 'none' : '';
        // Actif : masqué pour les équipes (toujours actives côté backend).
        const actGrp = document.getElementById('um-active-group');
        if (actGrp) actGrp.style.display = chk.checked ? 'none' : '';
        // Managers : visible UNIQUEMENT en mode équipe.
        const mgrGrp = document.getElementById('um-managers-group');
        if (mgrGrp) mgrGrp.style.display = chk.checked ? '' : 'none';
      });

    // Picker managers — reproduit exactement l'UX RACI "consultés" : le bouton
    // "+" ET tout le bloc raci-managers ouvrent le dropdown, avec curseur
    // pointer pour signaler l'affordance (cf. _wireActionModal,
    // affordance mai 2026). On câble ici localement plutôt que de dépendre
    // de _wireActionModal qui n'est appelé que depuis la modale d'action.
    const mgrPool = managerCandidates;
    const openMgrPicker = (anchor: HTMLElement): void => {
      showRaciPicker('managers', anchor, mgrPool);
    };
    document.getElementById('um-managers-add')?.addEventListener('click', (e: Event) => {
      e.stopPropagation();
      const btn = e.currentTarget as HTMLElement | null;
      if (btn) openMgrPicker(btn);
    });
    const mgrBlock = document.getElementById('raci-managers');
    if (mgrBlock) {
      mgrBlock.style.cursor = 'pointer';
      mgrBlock.addEventListener('click', (e: Event) => {
        const target = e.target as HTMLElement | null;
        if (!target) return;
        // Ignorer les clics qui ont leur propre handler (le "+" et les ✕ de
        // retrait de chips).
        if (target.closest('.raci-add-btn')) return;
        if (target.closest('[data-remove-raci]')) return;
        const anchor =
          mgrBlock.querySelector<HTMLElement>('.raci-add-btn') || (mgrBlock as HTMLElement);
        openMgrPicker(anchor);
      });
    }
    document
      .querySelectorAll<HTMLButtonElement>('#chips-managers [data-remove-raci]')
      .forEach((btn) => {
        btn.addEventListener('click', () =>
          _removeRaciChip('managers', parseInt(btn.dataset['removeRaci'] || '0')),
        );
      });

    // Générer / envoyer / copier le lien d'invitation → page-user-modal-invite.
    wireInvitationButtons(user);
  }, 0);
}

// ── ESM globals shim (lot B.8 + S4.2) ──
window.renderUserGroupsPage = renderUserGroupsPage;
window.showUserGroupModal = showUserGroupModal;
// showUserModal est typé via core-globals.d.ts en signature permissive
// (unknown) pour la consommation cross-module ; on cast à l'export.
window.showUserModal = showUserModal as unknown as typeof window.showUserModal;
// _permsByDomain : helper interne, exposé pour rétrocompat avec les .js
// éventuels. Pas typé sur Window (renamed _ pour signaler privé).
(window as unknown as { _permsByDomain: typeof _permsByDomain })._permsByDomain = _permsByDomain;

export {};
